import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Briefcase, DollarSign, MessageSquare, 
  Image, Settings, LogOut, Menu, X, ChevronDown, Users, FolderOpen,
  BookOpen, Sliders, Mail, LinkIcon, FileEdit, UserCheck, BarChart,
  HelpCircle, TrendingUp, Package, Target, Bell, ChevronLeft, ChevronRight,
  ExternalLink, LifeBuoy, Home, CreditCard, History, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { adminApi } from '@/lib/api';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    adminApi.verify()
      .then(res => setAdmin(res.data))
      .catch(() => {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      });
    
    // Fetch recent notifications (form submissions, new content)
    fetchNotifications();
  }, [navigate]);

  const fetchNotifications = async () => {
    try {
      const res = await adminApi.getFormSubmissions();
      const submissions = res.data || [];
      const newSubmissions = submissions.filter(s => s.status === 'new').slice(0, 5);
      setNotifications(newSubmissions.map(s => ({
        id: s.id,
        type: 'submission',
        title: 'New form submission',
        message: `${s.name} sent a message`,
        time: s.created_at,
        read: false
      })));
      setUnreadCount(newSubmissions.length);
    } catch (error) {
      console.log('Could not fetch notifications');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  // Main navigation items
  const menuItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Analytics', path: '/admin/analytics', icon: BarChart },
    { name: 'Pages', path: '/admin/pages', icon: FileEdit },
    { name: 'Component Library', path: '/admin/components', icon: Package },
    { name: 'A/B Tests', path: '/admin/ab-tests', icon: Target },
    { name: 'Blog Posts', path: '/admin/blog', icon: FileText },
    { name: 'Categories', path: '/admin/categories', icon: FolderOpen },
    { name: 'Services', path: '/admin/services', icon: Briefcase },
    { name: 'Workshop Management', path: '/admin/workshop-management', icon: Users },
    { name: 'Workshop Attendance', path: '/admin/workshop-attendance', icon: UserCheck },
    { name: 'Pricing Plans', path: '/admin/pricing', icon: DollarSign },
    { name: 'Testimonials', path: '/admin/testimonials', icon: Users },
    { name: 'Hero Slides', path: '/admin/slides', icon: Sliders },
    { name: 'Free Resources', path: '/admin/resources', icon: LinkIcon },
    { name: 'FAQ', path: '/admin/faq', icon: HelpCircle },
    { name: 'Team Members', path: '/admin/team', icon: Users },
    { name: 'Stats Blocks', path: '/admin/stats', icon: TrendingUp },
    { name: 'Form Submissions', path: '/admin/submissions', icon: Mail },
    { name: 'Media Library', path: '/admin/media', icon: Image },
    { name: 'Transactions', path: '/admin/transactions', icon: CreditCard },
    { name: 'Knowledge Base', path: '/admin/knowledge-base', icon: BookOpen },
  ];

  // Admin management items (Super Admin only)
  const adminManagementItems = [
    { name: 'Admin Users', path: '/admin/users', icon: Shield },
    { name: 'Audit Logs', path: '/admin/audit-logs', icon: History },
  ];

  // Bottom navigation items
  const bottomMenuItems = [
    { name: 'Site Settings', path: '/admin/settings', icon: Settings },
    { name: 'Help & Support', path: '/admin/help', icon: LifeBuoy },
  ];

  // Mobile bottom nav items (key features)
  const mobileNavItems = [
    { name: 'Home', path: '/admin', icon: Home },
    { name: 'Pages', path: '/admin/pages', icon: FileEdit },
    { name: 'Blog', path: '/admin/blog', icon: FileText },
    { name: 'Analytics', path: '/admin/analytics', icon: BarChart },
    { name: 'More', path: '#', icon: Menu, isMore: true },
  ];

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Desktop Top Header */}
      <header className={`
        hidden lg:flex fixed top-0 right-0 h-16 bg-white border-b z-40 items-center justify-between px-6
        transition-all duration-300
        ${sidebarOpen ? 'left-64' : 'left-20'}
      `}>
        <div className="flex items-center gap-4">
          <h2 className="font-medium text-muted-foreground">
            {menuItems.find(m => isActive(m.path))?.name || bottomMenuItems.find(m => isActive(m.path))?.name || 'Dashboard'}
          </h2>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Site Button */}
          <Button variant="outline" size="sm" asChild>
            <Link to="/" target="_blank" className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              View Site
            </Link>
          </Button>
          
          {/* Notifications Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs bg-red-500">
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                Notifications
                {unreadCount > 0 && (
                  <Badge variant="secondary">{unreadCount} new</Badge>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No new notifications
                </div>
              ) : (
                notifications.map((notif) => (
                  <DropdownMenuItem key={notif.id} asChild>
                    <Link to="/admin/submissions" className="flex flex-col items-start gap-1 p-3">
                      <span className="font-medium text-sm">{notif.title}</span>
                      <span className="text-xs text-muted-foreground">{notif.message}</span>
                    </Link>
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/admin/submissions" className="w-full text-center text-primary text-sm">
                  View all notifications
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* User Menu Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold text-sm">
                    {admin?.name?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                </div>
                <span className="hidden sm:inline text-sm font-medium">{admin?.name}</span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{admin?.name}</p>
                  <p className="text-xs text-muted-foreground">{admin?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/admin/settings" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/admin/help" className="flex items-center gap-2">
                  <LifeBuoy className="w-4 h-4" /> Help & Support
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b z-50 flex items-center justify-between px-4">
        <Link to="/admin" className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_career-platform-dev/artifacts/9caxsxh1_seg-career-support-logo.png" 
            alt="SEG" 
            className="h-8 w-auto"
          />
        </Link>
        <div className="flex items-center gap-2">
          {/* Mobile Notifications */}
          <Button variant="ghost" size="icon" className="relative" asChild>
            <Link to="/admin/submissions">
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-red-500">
                  {unreadCount}
                </Badge>
              )}
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-white border-r z-50 transition-all duration-300
        lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${sidebarOpen ? 'w-64' : 'lg:w-20'}
      `}>
        {/* Logo & Collapse Button */}
        <div className="h-16 flex items-center justify-between px-4 border-b">
          {sidebarOpen ? (
            <Link to="/" className="flex items-center" target="_blank">
              <img 
                src="https://customer-assets.emergentagent.com/job_career-platform-dev/artifacts/9caxsxh1_seg-career-support-logo.png" 
                alt="SEG Career Support" 
                className="h-10 w-auto"
              />
            </Link>
          ) : (
            <div className="w-full flex justify-center">
              <Link to="/admin" className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-bold">S</span>
              </Link>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex ml-auto"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="h-[calc(100vh-12rem)]">
          <nav className="p-3 space-y-1">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                  ${isActive(item.path) 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                title={!sidebarOpen ? item.name : undefined}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
            
            {/* Admin Management Section - Super Admin Only */}
            {admin?.role === 'super_admin' && (
              <>
                {sidebarOpen && (
                  <div className="pt-4 pb-2 px-3">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Admin Management
                    </span>
                  </div>
                )}
                {adminManagementItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                      ${isActive(item.path) 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                    `}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    title={!sidebarOpen ? item.name : undefined}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
                  </Link>
                ))}
              </>
            )}
          </nav>
        </ScrollArea>

        {/* Bottom Menu Items */}
        <div className="absolute bottom-20 left-0 right-0 px-3 space-y-1 border-t pt-3">
          {bottomMenuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                ${isActive(item.path) 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }
              `}
              title={!sidebarOpen ? item.name : undefined}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
            </Link>
          ))}
        </div>

        {/* User & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t bg-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold text-sm">
                {admin?.name?.charAt(0)?.toUpperCase() || 'A'}
              </span>
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{admin?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{admin?.email}</p>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className={`w-full ${sidebarOpen ? '' : 'px-0 justify-center'}`}
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {sidebarOpen && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t z-50 flex items-center justify-around px-2">
        {mobileNavItems.map((item) => (
          item.isMore ? (
            <button
              key={item.name}
              onClick={() => setMobileMenuOpen(true)}
              className="flex flex-col items-center justify-center gap-1 flex-1 py-2 text-muted-foreground"
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.name}</span>
            </button>
          ) : (
            <Link
              key={item.path}
              to={item.path}
              className={`
                flex flex-col items-center justify-center gap-1 flex-1 py-2
                ${isActive(item.path) ? 'text-primary' : 'text-muted-foreground'}
              `}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.name}</span>
            </Link>
          )
        ))}
      </nav>

      {/* Main Content */}
      <main className={`
        min-h-screen pt-16 pb-20 lg:pb-0 lg:pt-16 transition-all duration-300
        ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
      `}>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
